import { useEffect, useMemo, useState, lazy } from 'react';
import styles from './BrowseEvents.module.css';
import { Link, useLocation } from 'react-router-dom';
import FilterSection from '../components/FilterSection';
import { useSharedState } from '../hooks/useSharedState';
import { DateUtil } from '../utils/DateUtil';
import { timeOptions } from '../components/TimePicker';
import SearchBar from '../components/SearchBar';
import { DEFAULT_CITY } from '../utils/GlobalUtil';
import SubscriptionSection from '../components/SubscriptionSection';
import sortiesFamille from '../assets/sortiesFamille.jpg';
import activitesGratuites from '../assets/activitesGratuites.jpg';
import spectacleMusique from '../assets/spectacleMusique.jpg';
const EventList = lazy(() => import('../components/EventList')); // big list
import WeeklyHighlight from '../components/WeeklyHighlight';
import arrow from '../assets/arrowRight.png';
import { useEvents } from '../hooks/useEvents';
import { Popular } from '../components/Popular';
import { ScrollUtil } from '../utils/ScrollUtil';
import { usePopular } from '../hooks/usePopular';
import AppliedFilterSummary from '../components/AppliedFilterSummary';
import { AppliedFilterChip, getDefaultBrowseFilters } from '../utils/filterUtils';
import NoResultsRecovery from '../components/NoResultsRecovery';
import { buildEventListItems } from '../utils/eventAggregation';

function BrowseEvents() {

  const location = useLocation();

  const [categories, setCategories] = useSharedState('categories', '');
  const [free, setFree] = useSharedState('free', '');
  const [period, setPeriod] = useSharedState('period', '');
  const [time, setTime] = useSharedState('time', '');
  const [searchQuery, setSearchQuery] = useSharedState('searchQuery', '');
  const [city, setCity] = useSharedState('city', DEFAULT_CITY);
  const [venue, setVenue] = useSharedState('venue', '');
  const [latitude, setLatitude] = useSharedState('latitude', '');
  const [longitude, setLongitude] = useSharedState('longitude', '');

  const [initialized, setInitialized] = useState(false);
  const defaultFilters = getDefaultBrowseFilters();

  const filters = { categories, free, period, time, searchQuery, city, venue, latitude, longitude };
  const { events, loading } = useEvents(filters);
  const { events: popularEvents, loading: popularLoading } = usePopular();
  const listItems = useMemo(() => buildEventListItems(events), [events]);
  const searchSuggestionEvents = useMemo(() => {
    const uniqueEvents = new Map<number, typeof events[number]>();
    [...events, ...popularEvents].forEach((event) => {
      uniqueEvents.set(event.id, event);
    });
    return Array.from(uniqueEvents.values());
  }, [events, popularEvents]);

  const scrollStorageKey = useMemo(() => {
    return `${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  const getFilterStringKeyFromState = (): string => {
    const params = new URLSearchParams(location.search);

    params.set('cat', categories);
    params.set('f', free);
    params.set('p', period);
    params.set('t', time);
    params.set('q', searchQuery);
    params.set('c', city);
    params.set('v', venue);
    params.set('lat', latitude);
    params.set('long', longitude);

    const key = params.toString();
    return key;
  }

  //update url when state change
  useEffect(() => {
    if (!initialized) return;
    setUrlParamsFromState();
  }, [initialized, categories, free, period, time, searchQuery, city, venue, latitude, longitude]);

  //set state from url params
  useEffect(() => {

    const params = new URLSearchParams(location.search);

    const categoryParam = params.get('cat');
    if (categoryParam != null) {
      setCategories(categoryParam ?? categories);
    }

    const freeParam = params.get('f');
    setFree(freeParam == '1' ? '1' : '0');

    const periodParam = params.get('p');
    // si le param est null, on prend aujourd'hui par défaut
    setPeriod(periodParam ?? DateUtil.formatDateToLocalYMD(new Date()));

    const timeParam = params.get('t');
    if (timeParam || !time) {
      setTime(timeParam ?? timeOptions.TOUTE_HEURE);
    }

    const qParam = params.get('q');
    if (qParam != null) {
      setSearchQuery(qParam);
    }

    const cityParam = params.get('c');
    setCity(cityParam ?? DEFAULT_CITY);

    const venueParam = params.get('v');
    if (venueParam) {
      setVenue(venueParam ?? '');
    }

    const latParam = params.get('lat');
    if (latParam) {
      setLatitude(latParam ?? '');
    }

    const longParam = params.get('long');
    if (longParam) {
      setLongitude(longParam ?? '');
    }

    setInitialized(true);

  }, [location.search]); // Re-run if the location changes

  //smooth scroll to anchor (location.hash) where there is one
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  const setUrlParamsFromState = (): string => {
    const key = getFilterStringKeyFromState();
    window.history.replaceState({}, '', `${location.pathname}?${key}`);
    return key;
  };

  const quickSelect = (category: string) => {
    setCategories(category);
    const el = document.querySelector("#search");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const saveCurrentScroll = () => {
    ScrollUtil.saveLastBrowseLocation(scrollStorageKey);
  };

  const clearAllFilters = () => {
    setCategories(defaultFilters.categories);
    setFree(defaultFilters.free);
    setPeriod(defaultFilters.period);
    setTime(defaultFilters.time);
    setCity(defaultFilters.city);
    setVenue(defaultFilters.venue);
    setLatitude(defaultFilters.latitude);
    setLongitude(defaultFilters.longitude);
  };

  const removeSingleFilter = (chip: AppliedFilterChip) => {
    switch (chip.key) {
      case 'categories': {
        const remainingCategories = categories.split(';').filter(Boolean).filter((category) => category !== chip.value);
        setCategories(remainingCategories.join(';'));
        return;
      }
      case 'free':
        setFree('0');
        return;
      case 'period':
        setPeriod(defaultFilters.period);
        return;
      case 'time':
        setTime(defaultFilters.time);
        return;
      case 'location':
        setLatitude('');
        setLongitude('');
        return;
      case 'venue':
        setVenue('');
        return;
      default:
        return;
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setVenue('');
  };

  const resetSearchAndFilters = () => {
    clearSearch();
    clearAllFilters();
  };

  const applyCategorySuggestion = (slug: string) => {
    setCategories(slug);
    setSearchQuery('');
    setVenue('');
  };

  return (
    <>
      <div className={styles.quickSelect}>
        <div className={styles.categories}>
          <Link to="/ville-quebec/activites-familiales" reloadDocument className={styles.categoryLink}>
            <div className={styles.category}>
              <img src={sortiesFamille} alt="Sorties en famille" />
              <h2>Sorties <br /> en famille</h2>
            </div>
          </Link>
          <Link to="/ville-quebec/activites-gratuites" reloadDocument className={styles.categoryLink}>
            <div className={styles.category}>
              <img src={activitesGratuites} alt="Activités gratuites" />
              <h2>Activités <br /> gratuites</h2>
            </div>
          </Link>
          <Link to="#" className={styles.categoryLink}>
            <div className={styles.category} onClick={() => quickSelect('musique')}>
              <img src={spectacleMusique} alt="Musique" />
              <h2>Musique</h2>
            </div>
          </Link>
        </div>
      </div>

      <div className={styles.hero}>
        <h1>Événements à Québec : quoi faire aujourd’hui, cette semaine et ce mois-ci</h1>
        <h2>Concerts, humour, expositions, activités gratuites et idées de sorties à Québec, mis à jour chaque semaine.</h2>
        <p className={styles.todayLandingLink}>
          Tu cherches quoi faire à Québec aujourd'hui? <a href="/ville-quebec/aujourdhui">Consulte notre sélection du jour</a>.
        </p>
        <h2 className={styles.search} id="search">Tous les événements à Québec par catégorie</h2>
        <SearchBar suggestionEvents={searchSuggestionEvents} />
      </div>

      <FilterSection />
      <AppliedFilterSummary
        filters={{
          categories,
          free,
          period,
          time,
          city,
          venue,
          latitude,
          longitude,
        }}
        onRemoveChip={removeSingleFilter}
        onClearAll={clearAllFilters}
      />

      <Popular events={popularEvents} loading={popularLoading} onEventClick={saveCurrentScroll} />
      <div className={styles.resultsHeader}>
        <h2>Résultats</h2>
        {!loading && <p>{listItems.length} événement{listItems.length > 1 ? 's' : ''}</p>}
      </div>

      <EventList events={events} loading={loading} onEventClick={saveCurrentScroll} />
      {!loading && events.length == 0 && <NoResultsRecovery
        filters={{ categories, free, period, time, city, venue, latitude, longitude }}
        searchQuery={searchQuery}
        onClearSearch={clearSearch}
        onClearAll={resetSearchAndFilters}
        onApplyCategory={applyCategorySuggestion}
      />}

      <SubscriptionSection />
      <WeeklyHighlight />

      <div className={styles.callToAction}>
        <h2>Tu ne trouves pas l'événement que tu cherches?</h2>
        <h2>Tu aimerais ajouter un événement à cette page?</h2>
        <h2>Tu as trouvé une erreur?</h2>

        <a href="mailto:contact@alentours.ca"><button type="button" className={styles.callToActionBtn}>Écris-nous!</button></a>

        <img className={styles.arrow} src={arrow} alt="flèche" />

      </div>
    </>
  );
}

export default BrowseEvents
